import { Download, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";

import { useListDiff } from "../api/use-list-diff";
import { DiffFreshness } from "../components/DiffFreshness";
import { DiffHistoryDrawer } from "../components/DiffHistoryDrawer";
import { DiffKpiStrip } from "../components/DiffKpiStrip";
import { DiffTable } from "../components/DiffTable";
import {
  defaultDiffFilter,
  toggleSort,
  type DiffFilterState,
  type DiffSortKey,
} from "../lib/diff-filter";
import { diffToCsv, downloadCsv } from "../lib/export-diff-csv";

interface InventoryDiffPageProps {
  highlight?: string;
  /** From the route's URL state (spec D5: default on). */
  overOnly?: boolean;
  /** Writes the toggle back to the route's URL state, if the caller wants that. */
  onOverOnlyChange?: (overOnly: boolean) => void;
}

export function InventoryDiffPage({
  highlight,
  overOnly = true,
  onOverOnlyChange,
}: InventoryDiffPageProps): JSX.Element {
  const { t } = useTranslation(["inventory_diff", "nav", "common"]);
  const [filter, setFilter] = useState<DiffFilterState>(() => defaultDiffFilter(overOnly));
  const [searchDraft, setSearchDraft] = useState("");
  const [drawerSku, setDrawerSku] = useState<string | null>(null);
  const flashedRef = useRef<string | null>(null);

  const query = useListDiff(filter);
  const rows = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const total = query.data?.total ?? 0;
  const summary = query.data?.summary;

  // Deep-link flash: when rows are loaded and a highlight SKU is set, scroll
  // the matching row into view and apply the flash animation for ~2s.
  useEffect(() => {
    if (!highlight || rows.length === 0) return;
    // Only flash once per highlight value to avoid re-flashing on re-renders.
    if (flashedRef.current === highlight) return;
    flashedRef.current = highlight;

    const row = document.querySelector<HTMLElement>(`[data-testid="diff-row-${highlight}"]`);
    if (!row) return;

    row.scrollIntoView({ behavior: "smooth", block: "center" });
    row.classList.add("row-flash");
    const timer = setTimeout(() => {
      row.classList.remove("row-flash");
    }, 2000);
    return () => clearTimeout(timer);
  }, [highlight, rows]);

  const submitSearch = (): void =>
    setFilter((f) => ({ ...f, search: searchDraft.trim(), offset: 0 }));
  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }
  };
  const onSort = (key: DiffSortKey): void => setFilter((f) => toggleSort(f, key));

  return (
    <div data-testid="inventory-diff-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.operations")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("nav:item.inventory_diff")}</span>
          </>
        }
        title={t("inventory_diff:page.title")}
        subtitle={t("inventory_diff:page.subtitle", { count: summary?.skus_checked ?? total })}
        actions={
          <button
            type="button"
            onClick={() => downloadCsv("inventory-diff.csv", diffToCsv(rows))}
            data-testid="diff-export"
            className="inline-flex h-[var(--control-h)] items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] shadow-[var(--sh-xs)] transition hover:bg-[var(--canvas)] focus-ring"
          >
            <Download className="h-4 w-4" aria-hidden />
            {t("inventory_diff:toolbar.export")}
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t("inventory_diff:toolbar.search_placeholder")}
            data-testid="diff-search"
            className="h-[var(--control-h)] w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-[38px] pr-9 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring"
            style={{ boxShadow: "var(--sh-xs)" }}
          />
          {searchDraft && (
            <button
              type="button"
              onClick={() => {
                setSearchDraft("");
                setFilter((f) => ({ ...f, search: "", offset: 0 }));
              }}
              aria-label={t("common:action.clear")}
              data-testid="diff-search-clear"
              className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] focus-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>

        <label className="inline-flex h-[var(--control-h)] cursor-pointer select-none items-center gap-2 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-[13px] font-medium text-[var(--text-secondary)] shadow-[var(--sh-xs)] focus-within:border-[var(--brand-500)]">
          <input
            type="checkbox"
            checked={filter.overOnly}
            onChange={(e) => {
              setFilter((f) => ({ ...f, overOnly: e.target.checked, offset: 0 }));
              onOverOnlyChange?.(e.target.checked);
            }}
            data-testid="diff-over-only"
            className="h-4 w-4 accent-[var(--brand-500)] focus-ring"
          />
          {t("inventory_diff:toolbar.over_only")}
        </label>
      </div>

      {summary && <DiffKpiStrip summary={summary} />}
      {summary && <DiffFreshness summary={summary} />}

      {query.isError ? (
        <div
          data-testid="diff-error"
          className="rounded-[14px] border border-[var(--premium-border)] bg-white py-16 text-center text-sm text-[var(--text-primary)] shadow-[var(--sh-card)]"
        >
          {t("inventory_diff:state.error")}
        </div>
      ) : query.isLoading ? (
        <div
          data-testid="diff-loading"
          className="rounded-[14px] border border-[var(--premium-border)] bg-white p-4 shadow-[var(--sh-card)]"
        >
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-[44px] animate-pulse rounded-[10px] bg-[var(--surface-sunken)]"
              />
            ))}
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="diff-empty"
          className="rounded-[14px] border border-[var(--premium-border)] bg-white py-16 text-center shadow-[var(--sh-card)]"
        >
          <p className="text-sm font-medium text-[var(--text-primary)]">
            {t("inventory_diff:state.empty")}
          </p>
        </div>
      ) : (
        <>
          <DiffTable rows={rows} filter={filter} onSort={onSort} onRowClick={setDrawerSku} />
          <Pagination
            total={total}
            offset={filter.offset}
            limit={filter.limit}
            onChange={({ offset, limit }) => setFilter((f) => ({ ...f, offset, limit }))}
          />
        </>
      )}

      <DiffHistoryDrawer sku={drawerSku} onClose={() => setDrawerSku(null)} />
    </div>
  );
}
